// ---------------------------------------------------------------------------
// Persistent client-side diagnostics ring buffer (OPT-IN).
//
// Purpose: an overnight Firefox crash / session death cannot be watched live
// (the machine sleeps, taking any monitor with it) and the in-app addLog
// timeline is wiped by the crash. This buffer is written to localStorage, so it
// SURVIVES the reload/crash and can be read the next morning.
//
// Enable:  open the site once with  ?diag=1   (the flag is persisted).
// Disable: open the site with       ?diag=0
// Read (in the Firefox console):
//     copy(window.__avioDiag.text())        // full JSON to clipboard
//     window.__avioDiag.entries()           // array, for eyeballing
//     window.__avioDiag.clear()             // reset the buffer
//
// When disabled (the default for normal users) every diag() call is a cheap
// no-op and nothing is written, so this is safe to leave in production.
// ---------------------------------------------------------------------------

const KEY = 'aviotrack_diag';
const ENABLE_KEY = 'aviotrack_diag_enabled';
const MAX_ENTRIES = 2000;      // ~200 KB; well under the localStorage quota
const HEARTBEAT_MS = 30000;    // 30s cadence; sleep freezes it → gap detection

let enabled = false;
let seq = 0;
let buffer = [];
let heartbeatTimer = null;
let lastBeatAt = 0;
let probeFn = null;   // supplies live socket/session state for each heartbeat
let started = false;

function safeGet(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
function safeSet(k, v) { try { localStorage.setItem(k, v); } catch (_) { /* quota/full */ } }

function persist() {
  // Ring buffer: drop the oldest entries past the cap, then write.
  if (buffer.length > MAX_ENTRIES) buffer = buffer.slice(buffer.length - MAX_ENTRIES);
  safeSet(KEY, JSON.stringify(buffer));
}

export function isDiagEnabled() { return enabled; }

/**
 * Record one diagnostic event. `detail` is any small JSON-serialisable value.
 * No-op unless diagnostics have been enabled via ?diag=1.
 */
export function diag(event, detail) {
  if (!enabled) return;
  const entry = { i: ++seq, t: new Date().toISOString(), e: event };
  if (detail !== undefined) entry.d = detail;
  buffer.push(entry);
  persist();
}

/** Register a function returning live state to stamp into each heartbeat. */
export function setDiagProbe(fn) { probeFn = fn; }

function heartbeat() {
  const now = Date.now();
  const gap = lastBeatAt ? now - lastBeatAt : 0;
  lastBeatAt = now;
  // A gap far larger than the interval means timers were frozen — i.e. the
  // laptop slept/suspended. This is the marker to correlate the crash against.
  if (gap > HEARTBEAT_MS * 3) {
    diag('WAKE_GAP', { gapSec: Math.round(gap / 1000), note: 'timers were frozen (likely sleep/suspend)' });
  }
  // JS heap size is Chrome-only; Firefox does not expose it to content, so this
  // is null on FF — the WS event *counts* below are what reveal a reconnect
  // storm there, not heap numbers.
  let memMB = null;
  try {
    if (window.performance && window.performance.memory) {
      memMB = Math.round(window.performance.memory.usedJSHeapSize / 1048576);
    }
  } catch (_) { /* ignore */ }
  let probe = null;
  try { probe = probeFn ? probeFn() : null; } catch (_) { probe = null; }
  diag('BEAT', { hidden: document.hidden, memMB, ...(probe || {}) });
}

/**
 * Initialise diagnostics once, as early as possible at boot. Reads the ?diag
 * flag, restores any prior buffer (so entries accumulate across a crash+reload),
 * wires global error/lifecycle listeners, and starts the heartbeat.
 */
export function initDiag() {
  if (started) return;
  started = true;

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('diag') === '1') safeSet(ENABLE_KEY, '1');
    if (params.get('diag') === '0') { try { localStorage.removeItem(ENABLE_KEY); } catch (_) {} }
  } catch (_) { /* non-browser env */ }

  enabled = safeGet(ENABLE_KEY) === '1';
  if (!enabled) return;

  // Restore the prior buffer so a crash+reload keeps one continuous timeline.
  try {
    const prior = safeGet(KEY);
    if (prior) {
      buffer = JSON.parse(prior) || [];
      const last = buffer[buffer.length - 1];
      seq = (last && last.i) ? last.i : buffer.length;
    }
  } catch (_) { buffer = []; }

  diag('INIT', { ua: navigator.userAgent, url: window.location.href });

  // Global crash-adjacent capture — the errors/rejections most likely to
  // accompany a tab dying.
  window.addEventListener('error', (e) => {
    diag('JS_ERROR', {
      msg: String((e && e.message) || '').slice(0, 300),
      src: e && e.filename,
      line: e && e.lineno,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    let reason = '';
    try { reason = String((e.reason && (e.reason.message || e.reason)) || ''); } catch (_) { /* ignore */ }
    diag('UNHANDLED_REJECTION', { reason: reason.slice(0, 300) });
  });
  // Page Lifecycle signals: pagehide/freeze often precede a background tab
  // being discarded or crashed.
  window.addEventListener('pagehide', () => diag('PAGEHIDE', { persisted: false }));
  window.addEventListener('freeze', () => diag('FREEZE'));
  window.addEventListener('resume', () => diag('RESUME'));

  lastBeatAt = Date.now();
  heartbeatTimer = setInterval(heartbeat, HEARTBEAT_MS);
  heartbeat();

  // Morning-review helpers, available in the Firefox console.
  window.__avioDiag = {
    text: () => safeGet(KEY),
    entries: () => buffer.slice(),
    clear: () => { buffer = []; safeSet(KEY, '[]'); },
    stop: () => { if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; } },
    enabled: () => enabled,
  };
}
